import {
  ESCOPOS,
  SEVERIDADES,
  TIPOS_SALDO,
  type EscopoRegra,
  type Regra,
  type SeveridadeRegra,
  type TipoSaldo,
} from "../../lib/config-alertas/tipos";
import { formatarDecimal } from "../../lib/formato/decimal";
import { useTemPermissao } from "../../lib/auth/permissoes";

/*
 * Tabela de regras de alerta (FE-6b — Tela 6). Espelha a `<table>` de
 * `TabelaCanais`/`PaginaBancos`: colunas Nome · Tipo de saldo (rótulo) · Escopo
 * (rótulo) · Limite (money, à direita, SEM "R$") · Severidade (rótulo) · Ativa
 * (Sim/Não) · Ações.
 *
 * Ação "Editar" gated por `configurar_alertas`. Estado vazio: um `<p>`. O `limite`
 * é exibido por `formatarDecimal` (string-based) e alinhado à direita — NUNCA
 * convertido para `number`. Os rótulos vêm das tuplas (a chave técnica nunca
 * aparece sozinha). O gate é computado por hook NO CORPO (nunca dentro de `.map`).
 * SÓ tokens CPMH.
 */

const ACAO_CONFIGURAR_ALERTAS = "configurar_alertas";

/** Rótulo pt-BR de cada tipo de saldo. */
const ROTULO_TIPO_SALDO: Record<TipoSaldo, string> = TIPOS_SALDO.reduce(
  (mapa, opcao) => {
    mapa[opcao.valor] = opcao.rotulo;
    return mapa;
  },
  {} as Record<TipoSaldo, string>,
);

/** Rótulo pt-BR de cada escopo. */
const ROTULO_ESCOPO: Record<EscopoRegra, string> = ESCOPOS.reduce(
  (mapa, opcao) => {
    mapa[opcao.valor] = opcao.rotulo;
    return mapa;
  },
  {} as Record<EscopoRegra, string>,
);

/** Rótulo pt-BR de cada severidade. */
const ROTULO_SEVERIDADE: Record<SeveridadeRegra, string> = SEVERIDADES.reduce(
  (mapa, opcao) => {
    mapa[opcao.valor] = opcao.rotulo;
    return mapa;
  },
  {} as Record<SeveridadeRegra, string>,
);

interface TabelaRegrasProps {
  regras: Regra[];
  aoEditar: (regra: Regra) => void;
}

export function TabelaRegras({
  regras,
  aoEditar,
}: TabelaRegrasProps): JSX.Element {
  const podeConfigurar = useTemPermissao(ACAO_CONFIGURAR_ALERTAS);

  if (regras.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Nenhuma regra cadastrada.
      </p>
    );
  }

  return (
    <table className="w-full border-collapse font-corpo text-base text-off-black">
      <thead>
        <tr className="border-b border-platinum text-left">
          <th className="py-2 pr-4 font-subtitulo">Nome</th>
          <th className="py-2 pr-4 font-subtitulo">Tipo de saldo</th>
          <th className="py-2 pr-4 font-subtitulo">Escopo</th>
          <th className="py-2 pr-4 text-right font-subtitulo">Limite</th>
          <th className="py-2 pr-4 font-subtitulo">Severidade</th>
          <th className="py-2 pr-4 font-subtitulo">Ativa</th>
          <th className="py-2 pr-4 font-subtitulo">Ações</th>
        </tr>
      </thead>
      <tbody>
        {regras.map((regra) => (
          <tr key={regra.id} className="border-b border-platinum">
            <td className="py-2 pr-4">{regra.nome}</td>
            <td className="py-2 pr-4">{ROTULO_TIPO_SALDO[regra.tipo_saldo]}</td>
            <td className="py-2 pr-4">{ROTULO_ESCOPO[regra.escopo]}</td>
            <td className="py-2 pr-4 text-right">
              {formatarDecimal(regra.limite)}
            </td>
            <td className="py-2 pr-4">{ROTULO_SEVERIDADE[regra.severidade]}</td>
            <td className="py-2 pr-4">{regra.ativa ? "Sim" : "Não"}</td>
            <td className="py-2 pr-4">
              {podeConfigurar && (
                <button
                  type="button"
                  onClick={() => aoEditar(regra)}
                  className="text-flame hover:underline"
                >
                  Editar
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
